<template>
  <div
    class="select"
    :class="[
      `select--${theme}`,
      `select--${variant}`,
      { 'select--with-label': isLabelAvailable },
      { 'select--optional': !isRequired },
    ]"
  >
    <label
      class="select__label"
      :class="[{ 'select__label--hidden': !isLabelVisible }]"
      :for="idText"
      >{{ labelText }}</label
    >

    <select
      ref="select"
      :value="value"
      :id="idText"
      :name="nameText"
      :required="isRequired"
      :disabled="isDisabled"
      class="select__field"
      :class="{ 'select__field--with-placeholder': !isLabelVisible }"
      v-bind="$attrs"
      v-on="$listeners"
      @change="setValue($event.target.value)"
      @focus="setFocus(true)"
      @blur="setFocus(false)"
    >
      <option
        v-for="option in options"
        :value="option.value"
        :key="option.text"
        :selected="option.value === value"
        :disabled="option.disabled || false"
      >
        {{ option.text + getOptionalText(option.value) }}
      </option>
    </select>
    <i class="icon-chevron-new"></i>
    <slot name="below"></slot>
  </div>
</template>

<script>
import { getHash } from '@/helpers/number'
import propsSelect from '@/components/_shared/Form/Select/props-select'

export const PLACEHOLDER_VALUE = 0 // don't use empty string

export default {
  name: 'Select',

  // bind attributes to select (not to rool el) using v-bind="$attrs"
  inheritAttrs: false,

  props: { ...propsSelect },

  data() {
    return {
      hasFocus: false,
      hash: getHash(),
    }
  },

  computed: {
    idText() {
      return this.id || `${this.nameText}-${this.hash}`
    },

    nameText() {
      return this.label.replace(/\s+/g, '-').toLowerCase()
    },

    labelText() {
      return this.label
    },

    optionalText() {
      return !this.isRequired ? ` ${this.$t('checkout.address.optional')}` : ''
    },

    isLabelVisible() {
      return !this.isEmpty(this.value)
    },

    isLabelAvailable() {
      return !!this.label
    },

    getOptionalText() {
      return value => (this.isEmpty(value) ? this.optionalText : '')
    },
  },

  watch: {
    hasFocus: 'onFocusChange',

    value() {
      // fix to overwrite browser autocomplete value
      if (this.value !== undefined && this.value !== this.$refs.select.value)
        this.$refs.select.value = this.value
    },
  },

  methods: {
    getFieldEl() {
      return this.$refs.select
    },

    focus() {
      this.getFieldEl()?.focus()
    },

    isEmpty(value) {
      return value == PLACEHOLDER_VALUE
    },

    setValue(value) {
      this.$emit('update:value', value)
    },

    setFocus(hasFocus) {
      this.hasFocus = hasFocus
    },

    onFocusChange() {
      this.$emit('hasFocus', this.hasFocus)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@/components/_shared/Form/form-settings';

.select {
  position: relative;
  margin-top: $input-wrapper-margin-top;

  .icon-chevron-new {
    position: absolute;
    top: space(6);
    transform: translateY(-50%);
    right: 18px;
    margin-right: 0;
    color: $color-grey-dark-3;
    font-size: $font-size-xsmall;
    pointer-events: none;
  }

  &--with-label {
    margin-top: $input-wrapper-margin-top-with-label-mobile;

    @media ($mq-large) {
      margin-top: $input-wrapper-margin-top-with-label-desktop;
    }
  }

  &--dark {
    .select__field {
      // BG color should be defined by the element behind the field
      background-color: transparent;
      border-color: $color-white;
      color: $color-white;
    }

    .icon-chevron-new {
      color: $color-white;
    }
  }

  &--red {
    .select__field {
      border-color: $field-color-red;
    }
  }

  &--orange {
    .select__field {
      border-color: $field-color-orange;
    }
  }

  &__label {
    @include field-label;
  }

  &__field {
    position: relative;
    appearance: none;
    background: none;
    box-sizing: border-box;
    outline: none;
    height: $field-height;
    line-height: $field-height;
    width: $field-width;
    padding: 0 $field-padding-right 0 $field-padding-left;
    font-size: $field-font-size;
    border: $field-border-width solid $field-border-color;
    border-radius: 1px;
    color: $field-color;
    // BG color should be defined by the element behind the field
    background-color: transparent;
    transition: $field-transition;

    &:hover {
      cursor: pointer;
    }

    &:focus {
      border-color: $field-border-color-focused;
    }

    &:disabled {
      cursor: default;
      color: $color-grey-mid-2;
    }

    &::-ms-expand {
      display: none;
    }

    &:-moz-focusring {
      color: transparent;
      text-shadow: 0 0 0 $color-black;

      option:not(:checked) {
        color: $color-black; /* prevent <option>s from becoming transparent as well */
      }
    }

    &--with-placeholder {
      color: $color-grey-mid-2;
      font-size: $font-size-small;

      &:-moz-focusring {
        color: transparent;
        text-shadow: 0 0 0 $color-grey-mid-2;
      }
    }
  }
}

@-webkit-keyframes autofill {
  to {
    background-color: transparent;
  }
}

select:-webkit-autofill,
select:-webkit-autofill:hover,
select:-webkit-autofill:focus {
  -webkit-animation-name: autofill;
  -webkit-animation-fill-mode: both;
}
</style>
